const AUDIO_BPS = 2_000_000;
const VIDEO_BPS = 2_000_000;

export type RecordMode = 'VIDEO_ONLY' | 'AUDIO_ONLY' | 'FULL_VIDEO';

export interface TapeDeckOptions {
  file: string;
  mimeType: string;
  codec: string;
  extension: string;
  audioBitsPerSecond?: number;
  bitsPerSecond?: number;
}

const DEFAULT_OPTIONS: TapeDeckOptions = {
  file: 'MediaRecording_',
  mimeType: 'video/webm',
  codec: 'vp8',
  extension: 'webm',
  audioBitsPerSecond: AUDIO_BPS,
  bitsPerSecond: VIDEO_BPS,
};

/** The best Opus container this browser will accept, or `null` if none. */
function preferredAudioFormat(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const format of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported(format)) return format;
  }
  return null;
}

/**
 * Captures the synth output (and optionally the canvas) to a downloadable file.
 *
 * The original always constructed a `MediaSource` and an unused source buffer;
 * only the `MediaRecorder` path is actually needed, so that has been dropped.
 */
export class TapeDeck {
  static readonly RECORD_MODE = {
    VIDEO_ONLY: 'VIDEO_ONLY',
    AUDIO_ONLY: 'AUDIO_ONLY',
    FULL_VIDEO: 'FULL_VIDEO',
  } as const;

  static get isSupported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof window.MediaStreamAudioDestinationNode !== 'undefined'
    );
  }

  readonly input: MediaStreamAudioDestinationNode | GainNode;
  private options: TapeDeckOptions;
  private recorder: MediaRecorder | null = null;
  private videoStream: MediaStream | null = null;
  private blobs: Blob[] = [];

  constructor(
    context: AudioContext,
    options: Partial<TapeDeckOptions> = {},
    private recordMode: RecordMode = 'VIDEO_ONLY',
  ) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.input = TapeDeck.isSupported
      ? context.createMediaStreamDestination()
      : context.createGain();

    const audioFormat = preferredAudioFormat();
    if (this.recordMode === 'AUDIO_ONLY' && audioFormat) {
      const [mimeType, codec] = audioFormat.split(';codecs=');
      this.options.mimeType = mimeType;
      this.options.codec = codec;
    }
  }

  get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  assignVideoStream(canvas: HTMLCanvasElement, fps = 60): void {
    this.videoStream = canvas.captureStream(fps);
  }

  record(): void {
    if (!TapeDeck.isSupported || this.recording) return;
    this.blobs = [];

    const audioTracks =
      this.input instanceof MediaStreamAudioDestinationNode ? this.input.stream.getTracks() : [];
    const videoTracks = this.videoStream?.getTracks() ?? [];

    let tracks: MediaStreamTrack[];
    switch (this.recordMode) {
      case 'VIDEO_ONLY':
        tracks = videoTracks;
        break;
      case 'AUDIO_ONLY':
        tracks = audioTracks;
        break;
      default:
        tracks = [...audioTracks, ...videoTracks];
        break;
    }
    if (tracks.length === 0) return;

    this.recorder = new MediaRecorder(new MediaStream(tracks), {
      mimeType: `${this.options.mimeType};codecs=${this.options.codec}`,
      audioBitsPerSecond: this.options.audioBitsPerSecond,
    });
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.blobs.push(event.data);
    };
    this.recorder.start(100);
  }

  stop(): void {
    if (this.recorder?.state !== 'inactive') this.recorder?.stop();
  }

  /** Concatenates the captured chunks and triggers a browser download. */
  download(): void {
    if (this.blobs.length === 0) return;
    const blob = new Blob(this.blobs, { type: this.options.mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = `${this.options.file}${Date.now()}.${this.options.extension}`;
    document.body.append(link);
    link.click();
    setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(url);
    }, 1000);
  }

}
